import dynamic from "next/dynamic";
import { useRouter } from "next/router";
import { useCallback, useMemo, useState } from "react";

import { mapHero } from "./utils/mapHero";
import { normalizeOrderDetailsV2 } from "./utils/normalize";

import { useBreakpointV2 } from "@tm/ui-widgets";
import OrderDetailV2ActionModal from "./components/OrderDetailV2ActionModal";
import { usePolicyPolling } from "./hooks/usePolicyPolling";
import { OrderDetailV2Provider } from "./OrderDetailV2Context";
import type {
  OrderDetailV2ActionModalConfig,
  OrderDetailV2WrapperProps,
} from "./types";

// Lazy load device-specific components for code splitting
const OrderDetailV2Desktop = dynamic(() => import("./OrderDetailV2Desktop"), {
  ssr: true,
});

const OrderDetailV2PWA = dynamic(() => import("./OrderDetailV2PWA"), {
  ssr: true,
});

function OrderDetailV2Wrapper({
  data,
  showNav,
  orderActionHandlers,
}: OrderDetailV2WrapperProps) {
  const router = useRouter();

  // ── Polling & order data ───────────────────────────────────────────────
  const { orderData, refreshOrderDetails } = usePolicyPolling({
    data,
    routerQuery: router.query,
  });

  // ── Action modal state ─────────────────────────────────────────────────
  const [actionModal, setActionModal] =
    useState<OrderDetailV2ActionModalConfig | null>(null);

  const showActionModal = useCallback(
    (config: OrderDetailV2ActionModalConfig) => {
      setActionModal(config);
    },
    []
  );

  const openUrl = useCallback((url: string, openInNewTab: boolean) => {
    window.open(url, openInNewTab ? "_blank" : "_self");
  }, []);

  // ── Normalized data (memoized to avoid new object refs on every render) ─
  const currentNormalized = useMemo(
    () => normalizeOrderDetailsV2(orderData),
    [orderData]
  );

  const ctxValue = useMemo(() => {
    return {
      ...currentNormalized,
      refreshOrderDetails,
      orderActionHandlers,
      showActionModal,
      showNav,
      openUrl,
    };
  }, [
    currentNormalized,
    refreshOrderDetails,
    orderActionHandlers,
    showActionModal,
    showNav,
    openUrl,
  ]);

  const heroData = useMemo(
    () => mapHero(currentNormalized),
    [currentNormalized]
  );
  const device = useBreakpointV2();

  // ── Render ─────────────────────────────────────────────────────────────
  return (
    <OrderDetailV2Provider value={ctxValue}>
      {device === "lg" ? (
        <OrderDetailV2Desktop
          normalizedData={currentNormalized}
          heroData={heroData}
        />
      ) : (
        <OrderDetailV2PWA
          normalizedData={currentNormalized}
          heroData={heroData}
        />
      )}
      {actionModal && (
        <OrderDetailV2ActionModal
          config={actionModal}
          onClose={() => setActionModal(null)}
        />
      )}
    </OrderDetailV2Provider>
  );
}

export default OrderDetailV2Wrapper;
